/** Modal focus ownership for an overlay within the existing workspace shell. */
export function installMobileModal(
  panel: HTMLElement,
  background: HTMLElement[],
): (returnFocus?: boolean) => void {
  const document = panel.ownerDocument;
  const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const previous = background.map((element) => ({ element, inert: element.inert }));
  for (const { element } of previous) element.inert = true;
  const focusables = () =>
    Array.from(
      panel.querySelectorAll<HTMLElement>(
        'button:not(:disabled), a[href], input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((element) => !element.closest('[hidden], [inert]') && element.getClientRects().length);
  const trap = (event: KeyboardEvent) => {
    if (event.key !== 'Tab' || event.defaultPrevented) return;
    // A portaled menu or the unsaved-edit dialog owns its own focus scope.
    if (document.activeElement && !panel.contains(document.activeElement)) return;
    const items = focusables();
    const first = items[0];
    const last = items.at(-1);
    if (!first || !last) {
      event.preventDefault();
      panel.focus({ preventScroll: true });
    } else if (
      event.shiftKey &&
      (document.activeElement === first || !items.includes(document.activeElement as HTMLElement))
    ) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  document.addEventListener('keydown', trap);
  return (returnFocus = true) => {
    document.removeEventListener('keydown', trap);
    for (const { element, inert } of previous) element.inert = inert;
    // A responsive mode change keeps the panel mounted and its current focus.
    if (!returnFocus) return;
    // Wait until React has removed/replaced this canvas before returning focus.
    queueMicrotask(() => {
      const active = document.activeElement;
      if (active && active !== document.body && !panel.contains(active)) return;
      const fallback = document.querySelector<HTMLElement>(
        '.aui-composer-input, [data-testid="mobile-menu-button"]',
      );
      const target =
        trigger?.isConnected && !trigger.closest('[inert], [hidden]') ? trigger : fallback;
      target?.focus({ preventScroll: true });
    });
  };
}
